import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  FaBuilding, FaUserCheck, FaClipboardCheck, FaChartBar,
  FaCheckCircle, FaTimesCircle, FaExternalLinkAlt, FaSync,
  FaExclamationTriangle, FaUserCircle, FaCalendarAlt,
  FaPlus, FaBriefcase, FaFileInvoiceDollar, FaTimes, FaTrash, FaEdit,
  FaShieldAlt, FaFileAlt, FaClock, FaArrowRight, FaHardHat,
  FaChevronRight, FaMapMarkerAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import NotificationBell from '../../shared/NotificationBell';
import JobCard from '../../shared/JobCard';
import JobCalendar from '../../shared/JobCalendar';
import StatusBadge from '../../shared/JobStatusBadge';
import UserRoleBadge from '../../shared/UserRoleBadge';
import { openPdfInline } from '../../shared/PdfViewer/PdfViewerHost';
import PropertyCard from '../../property/PropertyCard/PropertyCard';
import ViewToggle from '../../shared/ViewToggle/ViewToggle';
import PropertyMap from '../../shared/PropertyMap/PropertyMap';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import FmDashboardClients from './FmDashboardClients';
import { isFlagEnabled, FLAGS } from '../../../utils/featureFlags';
import JobRequiredQualsPicker from '../../shared/QualificationPickers/JobRequiredQualsPicker';
import '../../pages/Dashboard/Dashboard.css';
import './FMDashboard.css';

const FMDashboard = () => {
  const navigate = useNavigate();
  const { propertyId, clientId } = useParams();
  // When the two-tier dashboard flag is on, the Clients view is the default
  // landing tab — Harvey's request. With the flag off, we keep the old
  // 'overview' default so existing tenants see no change. A clientId in the
  // URL (deep-linked client detail page) also forces the clients tab.
  const [activeTab, setActiveTab] = useState(() => (
    clientId || isFlagEnabled(FLAGS.FM_TWO_TIER_DASHBOARD) ? 'clients' : 'overview'
  ));
  useEffect(() => { if (clientId) setActiveTab('clients'); }, [clientId]);
  const [property, setProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId || null);
  const [checkIns, setCheckIns] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [jobs, setJobs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [contractorToRemove, setContractorToRemove] = useState(null);
  const [contractorToEdit, setContractorToEdit] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [conflictModal, setConflictModal] = useState(null); // { job, quoteId, count }
  const [jobFilter, setJobFilter] = useState('all');
  const [userName, setUserName] = useState('');
  const [propertiesSearchTerm, setPropertiesSearchTerm] = useState('');
  const [fmViewMode, setFmViewMode] = useState(() => sessionStorage.getItem('fm_view') || 'grid');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [quotedConfirmPending, setQuotedConfirmPending] = useState(null); // { action: 'edit'|'delete', job }

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const role = getUserRole();
    if (!['staff', 'admin', 'fm', 'super_admin'].includes(role)) {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchProperties();
    fetchContractors();
  }, [navigate]);

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const res = await fetch(getApiEndpoint('/api/users/profile'), { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const userData = await res.json();
          setUserName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'User');
        }
      } catch (_) { setUserName('User'); }
    };
    if (token) fetchUserName();
  }, [token]);

  useEffect(() => {
    if (selectedPropertyId) {
      fetchAllData();
      fetchJobs();
      fetchCalendar();
      fetchContractors();
    }
  }, [selectedPropertyId, selectedDate]);

  const fetchProperties = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/fm/properties'), { headers });
      if (res.ok) {
        const data = await res.json();
        setProperties(data);
        if (!selectedPropertyId && data.length > 0) {
          setSelectedPropertyId(data[0].id);
        }
      }
    } catch (e) {
      console.error('Failed to fetch properties:', e);
    }
  };

  const fetchContractors = async () => {
    try {
      const url = selectedPropertyId
        ? getApiEndpoint(`/api/jobs/contractors/list?property_id=${selectedPropertyId}`)
        : getApiEndpoint('/api/jobs/contractors/list');
      const res = await fetch(url, { headers });
      if (res.ok) setContractors(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchJobs = useCallback(async () => {
    try {
      let url = '/api/jobs?';
      if (selectedPropertyId) url += `property_id=${selectedPropertyId}&`;
      const res = await fetch(getApiEndpoint(url), { headers });
      if (res.ok) setJobs(await res.json());
    } catch (e) { console.error('Failed to fetch jobs:', e); }
  }, [selectedPropertyId]);

  const fetchCalendar = useCallback(async () => {
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      let url = `/api/jobs/calendar?start=${start}&end=${end}`;
      if (selectedPropertyId) url += `&property_id=${selectedPropertyId}`;
      const res = await fetch(getApiEndpoint(url), { headers });
      if (res.ok) setCalendarEvents(await res.json());
    } catch (e) { /* silent */ }
  }, [selectedPropertyId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, checkInsRes, testRes, complianceRes] = await Promise.all([
        fetch(getApiEndpoint(`/api/fm/properties/${selectedPropertyId}/overview`), { headers }),
        fetch(getApiEndpoint(`/api/fm/properties/${selectedPropertyId}/checkins?date=${selectedDate}`), { headers }),
        fetch(getApiEndpoint(`/api/fm/properties/${selectedPropertyId}/test-results?limit=100`), { headers }),
        fetch(getApiEndpoint(`/api/fm/properties/${selectedPropertyId}/compliance`), { headers })
      ]);
      if (overviewRes.ok) { const data = await overviewRes.json(); setOverview(data); setOverviewData(data); setProperty(data.property); }
      if (checkInsRes.ok) setCheckIns(await checkInsRes.json());
      if (testRes.ok) setTestResults(await testRes.json());
      if (complianceRes.ok) setCompliance(await complianceRes.json());
    } catch (e) {
      console.error('Failed to fetch FM data:', e);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out');
  };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';
  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const handleCreateJob = async (formData) => {
    try {
      const { required_qualifications, ...jobBody } = formData;
      const res = await fetch(getApiEndpoint('/api/jobs'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...jobBody, property_id: selectedPropertyId })
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || 'Failed to create job');
        return;
      }
      // Persist required qualifications (P1-14) if any were picked.
      const created = await res.json().catch(() => ({}));
      const newJobId = created?.id || created?.job?.id;
      if (newJobId && Array.isArray(required_qualifications) && required_qualifications.length > 0) {
        try {
          await fetch(getApiEndpoint(`/api/jobs/${newJobId}/required-qualifications`), {
            method: 'PUT',
            headers,
            body: JSON.stringify({ requirements: required_qualifications }),
          });
        } catch (_) { /* don't tank the create on this */ }
      }
      toast.success('Job created successfully');
      setShowCreateJob(false);
      fetchJobs();
      fetchCalendar();
    } catch (e) {
      toast.error('Failed to create job');
    }
  };

  const doQuoteAction = async (jobId, quoteId, action) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${jobId}/quote/${quoteId}`), {
        method: 'PATCH', headers, body: JSON.stringify({ status: action })
      });
      if (res.ok) {
        toast.success(`Quote ${action}`);
        fetchJobs();
        fetchCalendar();
        setSelectedJob(null);
        setConflictModal(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || `Failed to ${action} quote`);
      }
    } catch (e) { toast.error('Failed to respond to quote'); }
  };

  const handleQuoteAction = async (jobId, quoteId, action) => {
    if (action === 'declined') {
      await doQuoteAction(jobId, quoteId, action);
      return;
    }
    if (action === 'approved') {
      const job = selectedJob || jobs.find(j => j.id === jobId);
      if (!job?.contractor_company_id || !job?.scheduled_date) {
        await doQuoteAction(jobId, quoteId, action);
        return;
      }
      const dateStr = new Date(job.scheduled_date).toISOString().split('T')[0];
      try {
        const res = await fetch(getApiEndpoint(
          `/api/jobs/conflicts/check?contractor_id=${job.contractor_company_id}&date=${dateStr}&exclude_job_id=${jobId}`
        ), { headers });
        const data = res.ok ? await res.json() : { hasConflict: false };
        if (data.hasConflict) {
          setConflictModal({ job, quoteId, count: Number(data.count) || 1 });
          return;
        }
      } catch (_) { /* proceed if check fails */ }
      await doQuoteAction(jobId, quoteId, action);
    }
  };

  const handleRemoveContractor = async (contractor) => {
    if (!selectedPropertyId || !contractor) return;
    try {
      const res = await fetch(getApiEndpoint('/api/fm/contractors/remove'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ property_id: selectedPropertyId, contractor_user_id: contractor.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || 'Failed to remove contractor');
        return;
      }
      toast.success('Contractor removed from property');
      setContractorToRemove(null);
      fetchContractors();
    } catch (e) {
      toast.error('Failed to remove contractor');
    }
  };

  const activeVisitors = checkIns.filter(c => c.status === 'checked_in').length;
  const pendingQuotes = jobs.filter(j => j.status === 'quoted').length;
  const activeJobsCount = jobs.filter(j => j.status === 'active').length;

  const filteredJobs = jobFilter === 'all' ? jobs : jobs.filter(j => j.status === jobFilter);

  const LOCKED_STATUSES = ['approved', 'active', 'completed'];
  const canEditOrDeleteJob = (job) => !LOCKED_STATUSES.includes((job?.status || '').toLowerCase());
  const isQuoted = (job) => (job?.status || '').toLowerCase() === 'quoted';

  const handleJobEditClick = (job) => {
    if (isQuoted(job)) {
      setQuotedConfirmPending({ action: 'edit', job });
      return;
    }
    setJobToEdit(job);
  };

  const handleJobDeleteClick = (job) => {
    if (isQuoted(job)) {
      setQuotedConfirmPending({ action: 'delete', job });
      return;
    }
    setJobToDelete(job);
  };

  const handleQuotedConfirmProceed = () => {
    if (!quotedConfirmPending) return;
    const { action, job } = quotedConfirmPending;
    setQuotedConfirmPending(null);
    if (action === 'edit') setJobToEdit(job);
    else setJobToDelete(job);
  };

  const filteredPropertiesForView = propertiesSearchTerm.trim()
    ? properties.filter(p =>
        (p.name || '').toLowerCase().includes(propertiesSearchTerm.toLowerCase()) ||
        (p.address || '').toLowerCase().includes(propertiesSearchTerm.toLowerCase())
      )
    : properties;

  const handlePropertyClick = (propertyId) => {
    navigate(`/propertydashboard/${propertyId}`);
  };

  const handleFmViewChange = (mode) => {
    setFmViewMode(mode);
    sessionStorage.setItem('fm_view', mode);
  };

  const triggerFmGeocoding = useCallback(() => {
    // Re-fetch properties after geocoding
    const fetchProps = async () => {
      try {
        const res = await fetch(getApiEndpoint('/api/properties'), { headers });
        if (res.ok) {
          const data = await res.json();
          setProperties(data);
        }
      } catch (err) { /* ignore */ }
    };
    fetchProps();
  }, []);

  function renderPropertiesView() {
    return (
      <>
        <div className="dashboard-title-row">
          <div>
            <h1 className="dashboard-title">Welcome, {userName} 👋</h1>
            <p className="dashboard-subtitle">View and open any property (same view as owner)</p>
          </div>
          <button type="button" className="add-property-btn" onClick={() => navigate('/dashboard')}>
            Owner Dashboard
          </button>
        </div>
        <div className="search-section">
          <div className="search-bar search-bar-full-width">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search by property name or address..."
                value={propertiesSearchTerm}
                onChange={(e) => setPropertiesSearchTerm(e.target.value)}
                className="search-input"
              />
              {propertiesSearchTerm && (
                <button type="button" className="clear-search" onClick={() => setPropertiesSearchTerm('')}><FaTimes /></button>
              )}
            </div>
          </div>
        </div>
        <div className="properties-overview">
          <div className="dash-section-header">
            <h2 className="section-title" style={{ margin: 0 }}>Overview</h2>
            {filteredPropertiesForView.length > 0 && (
              <ViewToggle view={fmViewMode} onViewChange={handleFmViewChange} />
            )}
          </div>

          {fmViewMode === 'map' ? (
            <PropertyMap
              properties={filteredPropertiesForView}
              onPropertyClick={handlePropertyClick}
              triggerGeocoding={triggerFmGeocoding}
            />
          ) : (
            <div className="properties-grid">
              {filteredPropertiesForView.map(property => (
                <PropertyCard
                  key={property.id}
                  property={{ ...property, image_url: property.image_url || null }}
                  onPropertyClick={handlePropertyClick}
                  viewOnly
                />
              ))}
            </div>
          )}
          {filteredPropertiesForView.length === 0 && (
            <p className="fm-empty-state-text">No properties match your search.</p>
          )}
        </div>
      </>
    );
  }

  const UserAvatar = () => (
    <div className="user-avatar">
      <i className="fas fa-user" />
    </div>
  );

  return (
    <div className="main-container fm-dashboard">
      <header className="dashboard-header">
        <img src={cloudsafeLogo} alt="CloudSafe" className="logo" />
        <div className="header-right">
          <nav className="nav-links">
            {isFlagEnabled(FLAGS.FM_TWO_TIER_DASHBOARD) && (
              <button type="button" className={`nav-link ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')}>
                Clients
              </button>
            )}
            <button type="button" className={`nav-link ${activeTab === 'properties' ? 'active' : ''}`} onClick={() => setActiveTab('properties')}>
              Properties
            </button>
            <button type="button" className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              Overview
            </button>
            <button type="button" className={`nav-link ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>
              Jobs
              {pendingQuotes > 0 && <span className="fm-nav-badge">{pendingQuotes}</span>}
            </button>
            <button type="button" className={`nav-link ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
              Calendar
            </button>
            <button type="button" className={`nav-link ${activeTab === 'checkins' ? 'active' : ''}`} onClick={() => setActiveTab('checkins')}>
              Check-Ins
              {activeVisitors > 0 && <span className="fm-nav-badge">{activeVisitors}</span>}
            </button>
            <button type="button" className={`nav-link ${activeTab === 'tests' ? 'active' : ''}`} onClick={() => setActiveTab('tests')}>
              Tests
              {testResults.filter(t => t.mismatch_warning).length > 0 && (
                <span className="fm-nav-badge fm-nav-badge-warn">
                  {testResults.filter(t => t.mismatch_warning).length}
                </span>
              )}
            </button>
            <button type="button" className={`nav-link ${activeTab === 'other' ? 'active' : ''}`} onClick={() => setActiveTab('other')}>
              Other
            </button>
          </nav>
          <div className="header-icons">
            <UserRoleBadge />
            <select className="fm-property-select-in-header" value={selectedPropertyId || ''} onChange={(e) => setSelectedPropertyId(e.target.value ? parseInt(e.target.value) : null)}>
              <option value="">Select property...</option>
              {properties.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <button type="button" className="icon" onClick={() => { fetchAllData(); fetchJobs(); fetchCalendar(); }} title="Refresh"><FaSync /></button>
            <div className="icon"><NotificationBell /></div>
            <div className="user-avatar-container" aria-haspopup="menu" aria-expanded={showUserMenu} {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <UserAvatar />
              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/account-settings" onClick={() => setShowUserMenu(false)}>Account</Link>
                  <button type="button" onClick={() => { setShowUserMenu(false); handleLogout(); }}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile navigation — horizontal scrollable tab bar (≤900px) */}
      <div className="fm-mobile-nav-bar">
        {[
          ...(isFlagEnabled(FLAGS.FM_TWO_TIER_DASHBOARD) ? [{ id: 'clients', label: 'Clients' }] : []),
          { id: 'properties', label: 'Properties' },
          { id: 'overview', label: 'Overview' },
          { id: 'jobs', label: 'Jobs', badge: pendingQuotes },
          { id: 'calendar', label: 'Calendar' },
          { id: 'checkins', label: 'Check-Ins', badge: activeVisitors },
          { id: 'tests', label: 'Tests', badge: testResults.filter(t => t.mismatch_warning).length },
          { id: 'other', label: 'Other' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`fm-mobile-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.badge > 0 && <span className="fm-mobile-tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      <main className="dashboard-content">
        {activeTab === 'properties' && renderPropertiesView()}
        {activeTab !== 'properties' && (
          loading && activeTab === 'overview' ? (
            <div className="fm-loading"><div className="fm-spinner" /><p>Loading...</p></div>
          ) : (
            <>
              {activeTab === 'clients' && isFlagEnabled(FLAGS.FM_TWO_TIER_DASHBOARD) && <FmDashboardClients />}
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'jobs' && renderJobs()}
              {activeTab === 'calendar' && renderCalendar()}
              {activeTab === 'checkins' && renderCheckIns()}
              {activeTab === 'tests' && renderTestResults()}
              {activeTab === 'other' && renderOther()}
            </>
          )
        )}
      </main>

      {showCreateJob && <CreateJobModal properties={properties} contractors={contractors} selectedPropertyId={selectedPropertyId} onSubmit={handleCreateJob} onClose={() => setShowCreateJob(false)} />}
      {showAddContractor && <AddContractorModal selectedPropertyId={selectedPropertyId} properties={properties} onClose={() => setShowAddContractor(false)} onAdded={fetchContractors} headers={headers} />}
      {selectedJob && <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} onQuoteAction={handleQuoteAction} headers={headers} />}
      {conflictModal && (
        <div className="cs-modal-overlay" onClick={() => setConflictModal(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="cs-modal-header">
              <h3><FaExclamationTriangle style={{ color: '#d97706', marginRight: '0.5rem' }} /> Scheduling conflict</h3>
              <button type="button" className="cs-modal-close" onClick={() => setConflictModal(null)}><FaTimes /></button>
            </div>
            <div className="cs-modal-body">
              <p>
                This contractor already has {conflictModal.count === 1 ? '1 confirmed job' : `${conflictModal.count} confirmed jobs`} on this date.
              </p>
              <p style={{ fontSize: '0.88rem', color: '#6b7280' }}>Proceed with approving this quote anyway?</p>
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setConflictModal(null)}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-primary" onClick={() => doQuoteAction(conflictModal.job.id, conflictModal.quoteId, 'approved')}>
                <FaCheckCircle /> Confirm anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {contractorToRemove && (
        <div className="cs-modal-overlay" onClick={() => setContractorToRemove(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="cs-modal-header">
              <h3>Remove contractor</h3>
              <button type="button" className="cs-modal-close" onClick={() => setContractorToRemove(null)}><FaTimes /></button>
            </div>
            <div className="cs-modal-body">
              <p>Remove <strong>{contractorToRemove.company_name || `${contractorToRemove.first_name || ''} ${contractorToRemove.last_name || ''}`.trim() || 'this contractor'}</strong> from this property? They will no longer be available for jobs here.</p>
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setContractorToRemove(null)}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-primary" style={{ background: '#dc2626' }} onClick={() => handleRemoveContractor(contractorToRemove)}>Remove</button>
            </div>
          </div>
        </div>
      )}
      {contractorToEdit && (
        <div className="cs-modal-overlay" onClick={() => setContractorToEdit(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="cs-modal-header">
              <h3>Contractor details</h3>
              <button type="button" className="cs-modal-close" onClick={() => setContractorToEdit(null)}><FaTimes /></button>
            </div>
            <div className="cs-modal-body">
              <div className="fm-contractor-verify-box">
                <p className="fm-contractor-verify-label">Company / Contact</p>
                <p className="fm-contractor-verify-value">{contractorToEdit.company_name || `${(contractorToEdit.first_name || '').trim()} ${(contractorToEdit.last_name || '').trim()}`.trim() || '—'}</p>
                <p className="fm-contractor-verify-label">Email</p>
                <p className="fm-contractor-verify-value">{contractorToEdit.email || '—'}</p>
                <p className="fm-contractor-verify-label">Phone</p>
                <p className="fm-contractor-verify-value">{contractorToEdit.phone_number || '—'}</p>
                <p className="fm-contractor-verify-label">CloudSafe Company ID</p>
                <p className="fm-contractor-verify-value fm-contractor-verify-code">{contractorToEdit.company_code || '—'}</p>
              </div>
              <p className="fm-edit-hint">Company details are managed by the contractor in their account settings.</p>
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-primary" onClick={() => setContractorToEdit(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {quotedConfirmPending && (
        <div className="cs-modal-overlay" onClick={() => setQuotedConfirmPending(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="cs-modal-header">
              <h3>Quote already submitted</h3>
              <button className="cs-modal-close" onClick={() => setQuotedConfirmPending(null)}><FaTimes /></button>
            </div>
            <div className="cs-modal-body">
              <p>A quote has been submitted for this job. Making changes may require an additional add-on. Do you want to continue?</p>
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setQuotedConfirmPending(null)}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-primary" onClick={handleQuotedConfirmProceed}>Continue</button>
            </div>
          </div>
        </div>
      )}
      {jobToEdit && (
        <EditJobModal
          key={jobToEdit.id}
          job={jobToEdit}
          contractors={contractors}
          onSubmit={handleUpdateJob}
          onClose={() => setJobToEdit(null)}
        />
      )}
      {jobToDelete && (
        <div className="cs-modal-overlay" onClick={() => setJobToDelete(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="cs-modal-header">
              <h3>Delete job</h3>
              <button className="cs-modal-close" onClick={() => setJobToDelete(null)}><FaTimes /></button>
            </div>
            <div className="cs-modal-body">
              <p>Delete <strong>{jobToDelete.title}</strong>? This cannot be undone.</p>
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setJobToDelete(null)}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-danger" onClick={handleDeleteJob}><FaTrash /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderOverview() {
    const ov = overviewData || {};
    const comp = ov.compliance || compliance || {};
    const testR = comp.testResults || {};
    const docs = ov.documents || {};
    const compRate = comp.complianceRate || 0;
    const upcomingJobs = ov.upcomingJobs || [];
    const recentTests = ov.recentTests || [];
    const expiringDocs = docs.expiring || [];

    // Compliance ring SVG values
    const ringRadius = 54;
    const ringCircumference = 2 * Math.PI * ringRadius;
    const ringOffset = ringCircumference - (compRate / 100) * ringCircumference;
    const ringColor = compRate >= 80 ? '#059669' : compRate >= 50 ? '#d97706' : '#dc2626';

    return (
      <div className="fmo">
        {/* ─── Hero Banner ─── */}
        <div className="fmo-hero">
          <div className="fmo-hero-bg" style={{ backgroundImage: property?.image_url ? `url(${getApiEndpoint(`/api/uploads/${property.image_url.split('/').pop()}`)})` : 'none' }} />
          <div className="fmo-hero-overlay" />
          <div className="fmo-hero-content">
            <div className="fmo-hero-text">
              <h1 className="fmo-hero-title">{property?.name || 'Select a Property'}</h1>
              {property?.address && (
                <p className="fmo-hero-address"><FaMapMarkerAlt /> {property.address}</p>
              )}
            </div>
            <div className="fmo-hero-actions">
              <button className="fmo-hero-btn" onClick={() => setActiveTab('jobs')}><FaPlus /> Create Job</button>
              <button className="fmo-hero-btn fmo-hero-btn-outline" onClick={() => { fetchAllData(); fetchJobs(); fetchCalendar(); }}><FaSync /> Refresh</button>
            </div>
          </div>
        </div>

        {/* ─── KPI Strip ─── */}
        <div className="fmo-kpi-strip">
          <button type="button" className="fmo-kpi" onClick={() => setActiveTab('checkins')}>
            <div className="fmo-kpi-icon fmo-kpi-blue"><FaUserCheck /></div>
            <div className="fmo-kpi-data">
              <span className="fmo-kpi-value">{activeVisitors}</span>
              <span className="fmo-kpi-label">On Site Now</span>
            </div>
            {activeVisitors > 0 && <span className="fmo-kpi-pulse" />}
          </button>
          <button type="button" className="fmo-kpi" onClick={() => setActiveTab('jobs')}>
            <div className="fmo-kpi-icon fmo-kpi-purple"><FaBriefcase /></div>
            <div className="fmo-kpi-data">
              <span className="fmo-kpi-value">{activeJobsCount}</span>
              <span className="fmo-kpi-label">Active Jobs</span>
            </div>
          </button>
          <button type="button" className="fmo-kpi" onClick={() => setActiveTab('jobs')}>
            <div className="fmo-kpi-icon fmo-kpi-amber"><FaFileInvoiceDollar /></div>
            <div className="fmo-kpi-data">
              <span className="fmo-kpi-value">{pendingQuotes}</span>
              <span className="fmo-kpi-label">Pending Quotes</span>
            </div>
            {pendingQuotes > 0 && <span className="fmo-kpi-alert">{pendingQuotes}</span>}
          </button>
          <button type="button" className="fmo-kpi">
            <div className="fmo-kpi-icon fmo-kpi-green"><FaCheckCircle /></div>
            <div className="fmo-kpi-data">
              <span className="fmo-kpi-value">{testR.passed || 0}</span>
              <span className="fmo-kpi-label">Tests Passed</span>
            </div>
          </button>
          <button type="button" className="fmo-kpi">
            <div className="fmo-kpi-icon fmo-kpi-red"><FaTimesCircle /></div>
            <div className="fmo-kpi-data">
              <span className="fmo-kpi-value">{testR.failed || 0}</span>
              <span className="fmo-kpi-label">Tests Failed</span>
            </div>
          </button>
        </div>

        {/* ─── Main Grid ─── */}
        <div className="fmo-grid">
          {/* Compliance Ring */}
          <div className="fmo-panel fmo-compliance-panel">
            <div className="fmo-panel-header">
              <h3><FaShieldAlt /> Compliance</h3>
            </div>
            <div className="fmo-compliance-body">
              <div className="fmo-ring-wrap">
                <svg viewBox="0 0 128 128" className="fmo-ring-svg">
                  <circle cx="64" cy="64" r={ringRadius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
                  <circle cx="64" cy="64" r={ringRadius} fill="none" stroke={ringColor} strokeWidth="10"
                    strokeDasharray={ringCircumference} strokeDashoffset={ringOffset}
                    strokeLinecap="round" transform="rotate(-90 64 64)" className="fmo-ring-progress" />
                </svg>
                <div className="fmo-ring-label">
                  <span className="fmo-ring-pct">{compRate}%</span>
                  <span className="fmo-ring-sub">Pass Rate</span>
                </div>
              </div>
              <div className="fmo-compliance-stats">
                <div className="fmo-cs-row"><span className="fmo-cs-dot fmo-cs-green" /><span className="fmo-cs-text">Passed</span><span className="fmo-cs-val">{comp.passed || 0}</span></div>
                <div className="fmo-cs-row"><span className="fmo-cs-dot fmo-cs-red" /><span className="fmo-cs-text">Failed</span><span className="fmo-cs-val">{comp.failed || 0}</span></div>
                <div className="fmo-cs-row"><span className="fmo-cs-dot fmo-cs-gray" /><span className="fmo-cs-text">Total Assets</span><span className="fmo-cs-val">{comp.totalAssets || 0}</span></div>
              </div>
            </div>
          </div>

          {/* Upcoming Jobs Timeline */}
          <div className="fmo-panel fmo-jobs-panel">
            <div className="fmo-panel-header">
              <h3><FaCalendarAlt /> Upcoming Jobs</h3>
              <button className="fmo-view-all" onClick={() => setActiveTab('jobs')}>View All <FaChevronRight /></button>
            </div>
            <div className="fmo-panel-body">
              {upcomingJobs.length > 0 ? upcomingJobs.map((j, i) => {
                const jobDate = new Date(j.scheduled_date);
                const isToday = jobDate.toISOString().split('T')[0] === new Date().toISOString().split('T')[0];
                return (
                  <div key={j.id} className={`fmo-timeline-item ${isToday ? 'fmo-timeline-today' : ''}`}>
                    <div className="fmo-timeline-dot" />
                    {i < upcomingJobs.length - 1 && <div className="fmo-timeline-line" />}
                    <div className="fmo-timeline-content">
                      <div className="fmo-timeline-top">
                        <span className="fmo-timeline-title">{j.title}</span>
                        <span className={`fmo-timeline-badge fmo-badge-${j.status}`}>{j.status}</span>
                      </div>
                      <div className="fmo-timeline-meta">
                        <span>{isToday ? 'Today' : jobDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        {j.contractor_company && <span> &middot; {j.contractor_company}</span>}
                        <span className="fmo-timeline-area">{j.area}</span>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="fmo-empty-panel">
                  <FaBriefcase className="fmo-empty-icon" />
                  <p>No upcoming jobs this week</p>
                  <button className="fmo-empty-btn" onClick={() => { setActiveTab('jobs'); setShowCreateJob(true); }}><FaPlus /> Create Job</button>
                </div>
              )}
            </div>
          </div>

          {/* On Site Now */}
          <div className="fmo-panel fmo-onsite-panel">
            <div className="fmo-panel-header">
              <h3><FaHardHat /> On Site Now</h3>
              {activeVisitors > 0 && <span className="fmo-live-dot" />}
              <button className="fmo-view-all" onClick={() => setActiveTab('checkins')}>View All <FaChevronRight /></button>
            </div>
            <div className="fmo-panel-body">
              {(ov.recentCheckins || checkIns.filter(c => c.status === 'checked_in')).slice(0, 5).length > 0 ? (
                (ov.recentCheckins || checkIns.filter(c => c.status === 'checked_in')).slice(0, 5).map(c => (
                  <div key={c.id} className="fmo-person-row">
                    <div className="fmo-person-avatar">{(c.person_name || '?')[0].toUpperCase()}</div>
                    <div className="fmo-person-info">
                      <span className="fmo-person-name">{c.person_name}</span>
                      <span className="fmo-person-detail">{c.company || c.reason}</span>
                    </div>
                    <span className="fmo-person-time">{formatTime(c.check_in_time)}</span>
                  </div>
                ))
              ) : (
                <div className="fmo-empty-panel">
                  <FaUserCheck className="fmo-empty-icon" />
                  <p>No one currently on site</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Tests */}
          <div className="fmo-panel fmo-tests-panel">
            <div className="fmo-panel-header">
              <h3><FaClipboardCheck /> Recent Tests</h3>
            </div>
            <div className="fmo-panel-body">
              {recentTests.length > 0 ? recentTests.map(t => (
                <div key={t.id} className="fmo-test-row">
                  <span className={`fmo-test-dot ${t.status === 'Pass' ? 'fmo-test-pass' : 'fmo-test-fail'}`}>
                    {t.status === 'Pass' ? <FaCheckCircle /> : <FaTimesCircle />}
                  </span>
                  <div className="fmo-test-info">
                    <span className="fmo-test-ref">{t.reference_id}</span>
                    <span className="fmo-test-type">{(t.asset_type || '').replace(/_/g, ' ')}</span>
                  </div>
                  <span className="fmo-test-date">{formatDate(t.test_date)}</span>
                </div>
              )) : (
                <div className="fmo-empty-panel">
                  <FaClipboardCheck className="fmo-empty-icon" />
                  <p>No test results yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents Status */}
          <div className="fmo-panel fmo-docs-panel">
            <div className="fmo-panel-header">
              <h3><FaFileAlt /> Documents</h3>
              <button className="fmo-view-all" onClick={() => navigate(`/properties/${selectedPropertyId}/documents`)}>View All <FaChevronRight /></button>
            </div>
            <div className="fmo-panel-body">
              <div className="fmo-docs-summary">
                <div className="fmo-doc-stat">
                  <span className="fmo-doc-num">{docs.total || 0}</span>
                  <span className="fmo-doc-label">Total</span>
                </div>
                <div className="fmo-doc-stat fmo-doc-warn">
                  <span className="fmo-doc-num">{expiringDocs.length}</span>
                  <span className="fmo-doc-label">Expiring</span>
                </div>
                <div className="fmo-doc-stat fmo-doc-danger">
                  <span className="fmo-doc-num">{docs.expired || 0}</span>
                  <span className="fmo-doc-label">Expired</span>
                </div>
              </div>
              {expiringDocs.length > 0 && (
                <div className="fmo-expiring-list">
                  <span className="fmo-expiring-label"><FaClock /> Expiring Soon</span>
                  {expiringDocs.map(d => (
                    <div key={d.id} className="fmo-expiring-row">
                      <span className="fmo-expiring-name">{d.name}</span>
                      <span className="fmo-expiring-date">{formatDate(d.expiry_date)}</span>
                    </div>
                  ))}
                </div>
              )}
              {expiringDocs.length === 0 && docs.total === 0 && (
                <div className="fmo-empty-panel fmo-empty-sm">
                  <FaFileAlt className="fmo-empty-icon" />
                  <p>No documents uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="fmo-panel fmo-actions-panel">
            <div className="fmo-panel-header">
              <h3><FaArrowRight /> Quick Actions</h3>
            </div>
            <div className="fmo-panel-body fmo-quick-actions">
              <button className="fmo-action-card" onClick={() => { setActiveTab('jobs'); setShowCreateJob(true); }}>
                <div className="fmo-action-icon fmo-ai-blue"><FaPlus /></div>
                <span>Create Job</span>
              </button>
              <button className="fmo-action-card" onClick={() => setActiveTab('checkins')}>
                <div className="fmo-action-icon fmo-ai-green"><FaUserCheck /></div>
                <span>Check-Ins</span>
              </button>
              <button className="fmo-action-card" onClick={() => setActiveTab('calendar')}>
                <div className="fmo-action-icon fmo-ai-purple"><FaCalendarAlt /></div>
                <span>Calendar</span>
              </button>
              <button className="fmo-action-card" onClick={() => setActiveTab('other')}>
                <div className="fmo-action-icon fmo-ai-amber"><FaHardHat /></div>
                <span>Contractors</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderJobs() {
    return (
      <div className="fm-jobs">
        <div className="fm-toolbar">
          <h2>Job Orders</h2>
          <div className="toolbar-controls">
            <select className="cs-select" style={{ width: 'auto' }} value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
              <option value="all">All Jobs</option>
              <option value="invited">Invited</option>
              <option value="quoted">Quoted (Pending)</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowAddContractor(true)}>
              Add Contractor
            </button>
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => setShowCreateJob(true)}>
              <FaPlus /> Create Job
            </button>
          </div>
        </div>
        {filteredJobs.length > 0 ? (
          <div className="fm-jobs-list">
            {filteredJobs.map(j => (
              <JobCard
                key={j.id}
                job={j}
                onClick={() => fetchJobDetail(j.id)}
                onEdit={handleJobEditClick}
                onDelete={handleJobDeleteClick}
                canEdit={canEditOrDeleteJob(j)}
                canDelete={canEditOrDeleteJob(j)}
                editDisabledReason="This job can no longer be edited."
                deleteDisabledReason="This job can no longer be deleted."
              />
            ))}
          </div>
        ) : (
          <div className="fm-empty-state">
            <FaBriefcase className="empty-icon" />
            <h3>No Jobs Found</h3>
            <p>Create a job order to assign work to contractors.</p>
            <button className="cs-btn cs-btn-primary" onClick={() => setShowCreateJob(true)}><FaPlus /> Create Job</button>
          </div>
        )}
      </div>
    );
  }

  function renderCalendar() {
    return (
      <div className="fm-calendar-view">
        <div className="fm-toolbar">
          <h2>Job Calendar</h2>
          <div className="toolbar-controls">
            <div className="calendar-legend">
              <span className="legend-item"><span className="legend-dot" style={{background:'#e0e7ff'}} /> Invited</span>
              <span className="legend-item"><span className="legend-dot" style={{background:'#fef3c7'}} /> Quoted</span>
              <span className="legend-item"><span className="legend-dot" style={{background:'#d1fae5'}} /> Approved</span>
              <span className="legend-item"><span className="legend-dot" style={{background:'#dbeafe'}} /> Active</span>
            </div>
          </div>
        </div>
        <JobCalendar
          events={calendarEvents}
          onEventClick={(ev) => fetchJobDetail(ev.id)}
          onDateChange={(start, end) => {
            let url = `/api/jobs/calendar?start=${start}&end=${end}`;
            if (selectedPropertyId) url += `&property_id=${selectedPropertyId}`;
            fetch(getApiEndpoint(url), { headers }).then(r => r.ok ? r.json() : []).then(setCalendarEvents);
          }}
        />
      </div>
    );
  }

  async function fetchJobDetail(id) {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${id}`), { headers });
      if (res.ok) setSelectedJob(await res.json());
    } catch (e) { toast.error('Failed to load job detail'); }
  }

  const handleUpdateJob = async (payload) => {
    if (!jobToEdit?.id) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${jobToEdit.id}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update job');
      }
      setJobToEdit(null);
      fetchJobs();
      if (selectedJob?.id === jobToEdit.id) fetchJobDetail(jobToEdit.id);
      toast.success('Job updated');
    } catch (e) {
      toast.error(e.message || 'Failed to update job');
    }
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete?.id) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${jobToDelete.id}`), { method: 'DELETE', headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete job');
      }
      setJobToDelete(null);
      fetchJobs();
      if (selectedJob?.id === jobToDelete.id) setSelectedJob(null);
      toast.success('Job deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete job');
    }
  };

  function renderCheckIns() {
    return (
      <div className="fm-checkins">
        <div className="fm-toolbar">
          <h2>Building Check-Ins</h2>
          <div className="toolbar-controls">
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="date-picker" />
            <span className="count-badge">{checkIns.length} entries</span>
          </div>
        </div>
        <div className="fm-table-container">
          <table className="fm-table">
            <thead><tr><th>Name</th><th>Company / Role</th><th>Checked In By</th><th>Time In</th><th>Reason</th><th>Status</th><th>Time Out</th></tr></thead>
            <tbody>
              {checkIns.length > 0 ? checkIns.map(c => (
                <tr key={c.id} className={c.status === 'checked_in' ? 'row-active' : ''}>
                  <td className="name-cell"><FaUserCircle className="row-avatar" /><span>{c.person_name}</span></td>
                  <td>{c.company || c.person_role}</td>
                  <td>{c.checked_in_by_name}</td>
                  <td>{formatTime(c.check_in_time)}</td>
                  <td className="reason-cell">{c.reason}</td>
                  <td><StatusBadge status={c.status} size="small" /></td>
                  <td>{c.check_out_time ? formatTime(c.check_out_time) : '-'}</td>
                </tr>
              )) : <tr><td colSpan="7" className="fm-empty-row">No check-ins for this date</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderTestResults() {
    const mismatchCount = testResults.filter(t => t.mismatch_warning).length;
    const visible = mismatchOnly ? testResults.filter(t => t.mismatch_warning) : testResults;
    return (
      <div className="fm-tests">
        <div className="fm-toolbar">
          <h2>Hydrajaws Test Results</h2>
          <span className="count-badge">{testResults.length} results</span>
          {mismatchCount > 0 && (
            <button
              type="button"
              className={`fm-mismatch-toggle ${mismatchOnly ? 'active' : ''}`}
              onClick={() => setMismatchOnly(v => !v)}
              title="Test results submitted for assets not on the engineer's active job"
            >
              <FaExclamationTriangle /> {mismatchCount} mismatch{mismatchCount !== 1 ? 'es' : ''} {mismatchOnly ? '— showing only' : ''}
            </button>
          )}
        </div>
        <div className="fm-table-container">
          <table className="fm-table">
            <thead><tr><th></th><th>Reference ID</th><th>Asset Type</th><th>Test Date</th><th>Result</th><th>Report</th></tr></thead>
            <tbody>
              {visible.length > 0 ? visible.map(t => (
                <tr key={t.id} className={t.mismatch_warning ? 'fm-row-mismatch' : ''}>
                  <td>
                    {t.mismatch_warning && (
                      <span className="fm-mismatch-flag" title={t.mismatch_reason || 'Asset not on the engineer\'s active job'}>
                        <FaExclamationTriangle />
                      </span>
                    )}
                  </td>
                  <td className="ref-cell"><code>{t.reference_id}</code></td>
                  <td className="asset-type-cell">{(t.asset_type || '').replace(/_/g, ' ')}</td>
                  <td>{formatDate(t.test_date)}</td>
                  <td><span className={`result-badge ${t.status.toLowerCase()}`}>{t.status === 'Pass' ? <FaCheckCircle /> : <FaTimesCircle />} {t.status}</span></td>
                  <td>{t.pdf_url ? <button type="button" onClick={() => openPdfInline(t.pdf_url, `${(t.asset_type || 'Asset').replace(/_/g, ' ')} — Test Report`)} className="report-link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}><FaExternalLinkAlt /> View PDF</button> : '-'}</td>
                </tr>
              )) : <tr><td colSpan="6" className="fm-empty-row">No test results</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderCompliance() {
    if (!compliance) return <div className="fm-empty">Loading compliance data...</div>;
    const { totalAssets, assetStatus, testResults: tr } = compliance;
    const passRate = tr.total > 0 ? Math.round((tr.passed / tr.total) * 100) : 0;
    return (
      <div className="fm-compliance">
        <div className="fm-toolbar"><h2>Compliance Summary</h2></div>
        <div className="compliance-stats">
          <div className="compliance-card"><h3>Total Assets</h3><span className="comp-value">{totalAssets}</span></div>
          <div className="compliance-card pass"><h3>Passed</h3><span className="comp-value">{assetStatus.passed}</span></div>
          <div className="compliance-card fail"><h3>Failed</h3><span className="comp-value">{assetStatus.failed}</span></div>
          <div className="compliance-card pending"><h3>Pending</h3><span className="comp-value">{assetStatus.pending}</span></div>
          <div className="compliance-card unknown"><h3>Not Tested</h3><span className="comp-value">{assetStatus.unknown}</span></div>
        </div>
        <div className="compliance-meter">
          <h3>Test Pass Rate</h3>
          <div className="meter-bar"><div className="meter-fill" style={{ width: `${passRate}%` }} /></div>
          <div className="meter-labels"><span>{tr.total} total tests</span><span className="meter-rate">{passRate}%</span></div>
        </div>
        {assetStatus.failed > 0 && (
          <div className="compliance-alert"><FaExclamationTriangle /><span>{assetStatus.failed} asset(s) have failed status and require attention.</span></div>
        )}
      </div>
    );
  }

  function renderOther() {
    return (
      <div className="fm-other">
        <div className="fm-other-section">
          <div className="fm-other-section-header">
            <h3 className="fm-other-title">Contractors on this property</h3>
            {selectedPropertyId && (
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowAddContractor(true)}>
                <FaPlus /> Add Contractor
              </button>
            )}
          </div>
          {selectedPropertyId ? (
            contractors.length > 0 ? (
              <div className="fm-table-container">
                <table className="fm-table">
                  <thead>
                    <tr>
                      <th>Company / Contact</th>
                      <th>Email</th>
                      <th>CloudSafe Company ID</th>
                      <th className="fm-actions-th">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractors.map(c => (
                      <tr key={c.id}>
                        <td className="name-cell">
                          <FaUserCircle className="row-avatar" />
                          <span>{c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}</span>
                        </td>
                        <td>{c.email || '—'}</td>
                        <td><code className="fm-company-code">{c.company_code || '—'}</code></td>
                        <td className="fm-actions-cell">
                          <button type="button" className="fm-btn-icon fm-btn-edit" onClick={() => setContractorToEdit(c)} title="View / Edit details"><FaEdit /></button>
                          <button type="button" className="fm-btn-icon fm-btn-remove" onClick={() => setContractorToRemove(c)} title="Remove from property"><FaTrash /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="fm-empty-state fm-other-empty">
                <FaBriefcase className="empty-icon" />
                <p>No contractors added to this property yet.</p>
                <button type="button" className="cs-btn cs-btn-primary" onClick={() => setShowAddContractor(true)}>
                  <FaPlus /> Add Contractor
                </button>
              </div>
            )
          ) : (
            <p className="fm-other-select-hint">Select a property in the header to manage contractors.</p>
          )}
        </div>
      </div>
    );
  }
};

/* ─── Add Contractor Modal (lookup then confirm, same workflow as owner add FM) ─── */
const AddContractorModal = ({ selectedPropertyId, properties, onClose, onAdded, headers }) => {
  const [companyCode, setCompanyCode] = useState('');
  const [step, setStep] = useState('enter');
  const [verified, setVerified] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const propertyName = properties.find(p => p.id === selectedPropertyId)?.name || '';

  const handleLookup = async (e) => {
    e.preventDefault();
    const code = companyCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('CloudSafe Company ID must be exactly 6 digits');
      return;
    }
    if (!selectedPropertyId) {
      setError('Please select a property in the header first.');
      return;
    }
    setError('');
    setVerified(null);
    setSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/fm/contractors/lookup?company_code=${encodeURIComponent(code)}`), { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.found) {
        setVerified({ company_name: data.company_name, company_code: data.company_code });
        setStep('verify');
      } else {
        setError(data.message || 'No contractor company found with this CloudSafe Company ID');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAdd = async (e) => {
    e.preventDefault();
    if (!verified || !selectedPropertyId) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint('/api/fm/contractors/add'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ company_code: verified.company_code, property_id: selectedPropertyId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to add contractor');
        setSubmitting(false);
        return;
      }
      toast.success(data.message || 'Contractor added');
      onAdded();
      onClose();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep('enter');
    setVerified(null);
    setError('');
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="cs-modal-header">
          <h3>{step === 'verify' ? 'Confirm contractor' : 'Add Contractor'}</h3>
          <button type="button" className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={handleLookup}>
            <div className="cs-modal-body">
              <p className="fm-add-contractor-hint">Enter the contractor company&apos;s 6-digit CloudSafe Company ID. Once added, they will be available for jobs on this property.</p>
              {!selectedPropertyId ? (
                <p className="fm-add-contractor-no-property">Please select a property in the header first.</p>
              ) : (
                <>
                  <div className="cs-form-group">
                    <label>Property</label>
                    <div className="cs-input cs-readonly">{propertyName}</div>
                  </div>
                  <div className="cs-form-group">
                    <label>CloudSafe Company ID (6 digits) *</label>
                    <input
                      type="text"
                      className="cs-input"
                      placeholder="e.g. 123456"
                      value={companyCode}
                      onChange={e => setCompanyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      autoComplete="off"
                    />
                  </div>
                  {error && <p className="fm-add-contractor-error">{error}</p>}
                </>
              )}
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="cs-btn cs-btn-primary" disabled={!selectedPropertyId || submitting}>
                {submitting ? 'Looking up…' : 'Find contractor'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmAdd}>
            <div className="cs-modal-body">
              <p className="fm-add-contractor-hint">Please confirm this is the correct contractor before adding them to the property.</p>
              <div className="cs-form-group">
                <label>Property</label>
                <div className="cs-input cs-readonly">{propertyName}</div>
              </div>
              <div className="fm-contractor-verify-box">
                <p className="fm-contractor-verify-label">Company name</p>
                <p className="fm-contractor-verify-value">{verified?.company_name}</p>
                <p className="fm-contractor-verify-label">CloudSafe Company ID</p>
                <p className="fm-contractor-verify-value fm-contractor-verify-code">{verified?.company_code}</p>
              </div>
              {error && <p className="fm-add-contractor-error">{error}</p>}
            </div>
            <div className="cs-modal-footer">
              <button type="button" className="cs-btn cs-btn-outline" onClick={handleBack}>Back</button>
              <button type="submit" className="cs-btn cs-btn-primary" disabled={submitting}>
                {submitting ? 'Adding…' : 'Confirm & add to property'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─── Create Job Modal ─────────────────────────── */
const CreateJobModal = ({ contractors, selectedPropertyId, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    area: '', title: '', description: '', scheduled_date: '', contractor_company_id: ''
  });
  const [requiredQuals, setRequiredQuals] = useState([]); // [{qualification_id, min_count}]

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.area || !form.title || !form.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    onSubmit({
      ...form,
      contractor_company_id: form.contractor_company_id ? parseInt(form.contractor_company_id) : null,
      required_qualifications: requiredQuals,
    });
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3>Create Job Order</h3>
          <button className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="cs-modal-body">
            <div className="cs-form-group">
              <label>Area *</label>
              <select className="cs-select" value={form.area} onChange={e => setForm({...form, area: e.target.value})} required>
                <option value="">Select area...</option>
                <option value="Roof Safety">Roof Safety</option>
                <option value="Fire Safety">Fire Safety</option>
                <option value="Electrical Safety">Electrical Safety</option>
                <option value="General Maintenance">General Maintenance</option>
                <option value="Building Maintenance">Building Maintenance</option>
                <option value="Equipment Safety">Equipment Safety</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="cs-form-group">
              <label>Job Title *</label>
              <input className="cs-input" placeholder="e.g. Annual Anchor Point Testing" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
            </div>
            <div className="cs-form-group">
              <label>Description</label>
              <textarea className="cs-textarea" placeholder="Describe the work required..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="cs-form-group">
              <label>Scheduled Date *</label>
              <input type="date" className="cs-input" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} required />
            </div>
            <div className="cs-form-group">
              <label>Assign Contractor</label>
              <select className="cs-select" value={form.contractor_company_id} onChange={e => setForm({...form, contractor_company_id: e.target.value})}>
                <option value="">Select contractor (optional)...</option>
                {contractors.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`} ({c.email})</option>
                ))}
              </select>
            </div>
            <div className="cs-form-group">
              <label>Required qualifications</label>
              <JobRequiredQualsPicker value={requiredQuals} onChange={setRequiredQuals} />
            </div>
          </div>
          <div className="cs-modal-footer">
            <button type="button" className="cs-btn cs-btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="cs-btn cs-btn-primary"><FaPlus /> Create Job</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Edit Job Modal ─────────────────────────── */
const EditJobModal = ({ job, contractors, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    area: job?.area || '',
    title: job?.title || '',
    description: job?.description || '',
    scheduled_date: job?.scheduled_date ? new Date(job.scheduled_date).toISOString().slice(0, 10) : '',
    // The picker lists contractor USERS; jobs now store a company id, so preselect contractor_user_id.
    // Only fall back to contractor_company_id for old responses that lack contractor_user_id entirely.
    contractor_company_id: (() => {
      const uid = job && Object.prototype.hasOwnProperty.call(job, 'contractor_user_id') ? job.contractor_user_id : job?.contractor_company_id;
      return uid ? String(uid) : '';
    })()
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.area || !form.title || !form.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    onSubmit({
      area: form.area,
      title: form.title,
      description: form.description || null,
      scheduled_date: form.scheduled_date
    });
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3>Edit Job Order</h3>
          <button className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="cs-modal-body">
            <div className="cs-form-group">
              <label>Area *</label>
              <select className="cs-select" value={form.area} onChange={e => setForm({...form, area: e.target.value})} required>
                <option value="">Select area...</option>
                <option value="Roof Safety">Roof Safety</option>
                <option value="Fire Safety">Fire Safety</option>
                <option value="Electrical Safety">Electrical Safety</option>
                <option value="General Maintenance">General Maintenance</option>
                <option value="Building Maintenance">Building Maintenance</option>
                <option value="Equipment Safety">Equipment Safety</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="cs-form-group">
              <label>Job Title *</label>
              <input className="cs-input" placeholder="e.g. Annual Anchor Point Testing" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
            </div>
            <div className="cs-form-group">
              <label>Description</label>
              <textarea className="cs-textarea" placeholder="Describe the work required..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="cs-form-group">
              <label>Scheduled Date *</label>
              <input type="date" className="cs-input" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} required />
            </div>
            <div className="cs-form-group">
              <label>Assign Contractor</label>
              <select className="cs-select" value={form.contractor_company_id} onChange={e => setForm({...form, contractor_company_id: e.target.value})}>
                <option value="">Select contractor (optional)...</option>
                {(contractors || []).map(c => (
                  <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`} ({c.email})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="cs-modal-footer">
            <button type="button" className="cs-btn cs-btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="cs-btn cs-btn-primary"><FaEdit /> Save changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Job Detail Modal ─────────────────────────── */
const JobDetailModal = ({ job, onClose, onQuoteAction, headers }) => {
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <div className="cs-modal-header">
          <h3>{job.title}</h3>
          <button className="cs-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="cs-modal-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <StatusBadge status={job.status} />
            <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{job.area}</span>
          </div>

          {job.description && <p style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '1rem', lineHeight: 1.5, textAlign: 'left' }}>{job.description}</p>}

          <div className="fm-job-detail-grid">
            <div className="detail-item"><strong>Property:</strong> {job.property_name}</div>
            <div className="detail-item"><strong>Date:</strong> {new Date(job.scheduled_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
            {job.contractor_first_name && <div className="detail-item"><strong>Contractor:</strong> {job.contractor_first_name} {job.contractor_last_name}</div>}
            {job.duration_minutes && <div className="detail-item"><strong>Duration:</strong> {Math.floor(job.duration_minutes / 60)}h {job.duration_minutes % 60}m</div>}
          </div>

          {job.quotes && job.quotes.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Quotes</h4>
              {job.quotes.map(q => {
                const quoteDocs = (job.attachments || []).filter(a => a.quote_id === q.id);
                return (
                  <div key={q.id} className="fm-quote-card">
                    <div className="fm-quote-top">
                      <span className="fm-quote-price">{q.price > 0 ? `£${parseFloat(q.price).toFixed(2)}` : 'Quote document'}</span>
                      <StatusBadge status={q.status} size="small" />
                    </div>
                    <span className="fm-quote-from">{q.first_name} {q.last_name}</span>
                    {q.message && <p className="fm-quote-msg">{q.message}</p>}
                    {q.notes && <p className="fm-quote-notes">{q.notes}</p>}
                    {quoteDocs.length > 0 && (
                      <div className="fm-quote-documents">
                        <strong className="fm-quote-docs-label">Quote document(s)</strong>
                        {quoteDocs.map(doc => {
                          const docUrl = (doc.download_url ? getApiEndpoint(doc.download_url) : undefined);
                          const isPdf = (doc.mime_type || '').toLowerCase().includes('pdf') || (doc.original_filename || '').toLowerCase().endsWith('.pdf');
                          return (
                            <div key={doc.id} className="fm-quote-doc">
                              <a href={docUrl} target="_blank" rel="noopener noreferrer" className="fm-quote-doc-link">
                                <FaExternalLinkAlt /> {doc.original_filename || doc.stored_filename}
                              </a>
                              {isPdf && (
                                <iframe title={doc.original_filename || 'Quote PDF'} src={docUrl} className="fm-quote-pdf-embed" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {q.status === 'pending' && (
                      <div className="fm-quote-actions">
                        <button className="cs-btn cs-btn-success cs-btn-sm" onClick={() => onQuoteAction(job.id, q.id, 'approved')}>
                          <FaCheckCircle /> Approve
                        </button>
                        <button className="cs-btn cs-btn-danger cs-btn-sm" onClick={() => onQuoteAction(job.id, q.id, 'declined')}>
                          <FaTimesCircle /> Decline
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {job.staff && job.staff.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Assigned Staff</h4>
              {job.staff.map(s => (
                <div key={s.id} className="fm-staff-item">
                  <FaUserCircle style={{ color: '#d1d5db', fontSize: '1.2rem' }} />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.first_name} {s.last_name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block' }}>
                      {s.signed_in_at ? `Signed in ${new Date(s.signed_in_at).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'})}` : 'Not signed in'}
                      {s.signed_out_at && ` — Out ${new Date(s.signed_out_at).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'})}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {job.notes && job.notes.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Notes & Activity</h4>
              {job.notes.map(n => (
                <div key={n.id} className="fm-note-item">
                  <span className="fm-note-author">{n.first_name} {n.last_name}</span>
                  <p className="fm-note-text">{n.note_text}</p>
                  <span className="fm-note-time">{new Date(n.created_at).toLocaleString('en-GB')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FMDashboard;
